import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  OnDestroy,
  Input,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';

@Component({
  selector: 'app-splash-screen',
  templateUrl: './splash-screen.component.html',
  styleUrls: ['./splash-screen.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SplashScreenComponent implements OnInit, OnDestroy, OnChanges {
  /** Emitted once the full animation sequence (including fade out) is complete */
  @Output() animationComplete = new EventEmitter<void>();

  /** Input to signal that background loading is done and we can exit */
  @Input() stopLoading = false;

  phase: 'logo' | 'text' | 'pulse' | 'fadeout' = 'logo';
  isVisible = true;

  private timers: ReturnType<typeof setTimeout>[] = [];
  private hasStartedExit = false;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.runIntroSequence();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['stopLoading']?.currentValue === true) {
      this.tryExit();
    }
  }

  private runIntroSequence(): void {
    // Phase 1: logo bounces in (0ms – 800ms)
    
    // Phase 2: text fades in at 700ms
    this.schedule(() => {
      this.phase = 'text';
      this.cdr.markForCheck();
    }, 700);

    // Phase 3: switch to pulse at 1800ms
    // We stay in 'pulse' phase indefinitely until stopLoading is true
    this.schedule(() => {
      this.phase = 'pulse';
      this.cdr.markForCheck();
      // If loading is already done by the time we hit this phase, exit immediately
      if (this.stopLoading) {
        this.tryExit();
      }
    }, 1800);
  }

  /** Triggers the exit animation sequence */
  private tryExit(): void {
    // Only exit if we've reached at least the pulse phase to avoid visual glitches
    if (this.hasStartedExit || (this.phase !== 'pulse' && this.phase !== 'text')) {
      return;
    }

    this.hasStartedExit = true;
    this.phase = 'fadeout';
    this.cdr.markForCheck();

    // After fadeout CSS transition finishes (600ms in SCSS)
    this.schedule(() => {
      this.isVisible = false;
      this.cdr.markForCheck();
      this.animationComplete.emit();
    }, 700);
  }

  private schedule(fn: () => void, ms: number): void {
    this.timers.push(setTimeout(fn, ms));
  }

  ngOnDestroy(): void {
    this.timers.forEach(clearTimeout);
  }
}

